import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateMappingElementMutationRequest,
  UpdateMappingElementMutationResponse,
} from "../types/UpdateMappingElement"

type UpdateMappingElementClient = typeof client<
  UpdateMappingElementMutationResponse,
  Error,
  UpdateMappingElementMutationRequest
>
type UpdateMappingElement = {
  data: UpdateMappingElementMutationResponse
  error: Error
  request: UpdateMappingElementMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateMappingElementClient>>
  client: {
    parameters: Partial<Parameters<UpdateMappingElementClient>[0]>
    return: Awaited<ReturnType<UpdateMappingElementClient>>
  }
}
/**
 * @description Update a mapping element
 * @link /v1/client-settings/update-mapping-element
 */
export function useUpdateMappingElement(
  options: {
    mutation?: UseMutationOptions<
      UpdateMappingElement["response"],
      UpdateMappingElement["error"],
      UpdateMappingElement["request"]
    >
    client?: UpdateMappingElement["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateMappingElement["response"],
  UpdateMappingElement["error"],
  UpdateMappingElement["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateMappingElement["response"],
    UpdateMappingElement["error"],
    UpdateMappingElement["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateMappingElement["data"],
        UpdateMappingElement["error"],
        UpdateMappingElement["request"]
      >({
        method: "post",
        url: `/v1/client-settings/update-mapping-element`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
